import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export type ThemeVariant = "assembly" | "classic" | "modern" | "cyberpunk" | "indie";

export const THEME_VARIANTS: { value: ThemeVariant; label: string }[] = [
  { value: "assembly", label: "Assembly" },
  { value: "classic", label: "Classic" },
  { value: "modern", label: "Modern" },
  { value: "cyberpunk", label: "Cyberpunk" },
  { value: "indie", label: "Indie" },
];

interface ThemeVariantContextValue {
  variant: ThemeVariant;
  setVariant: (variant: ThemeVariant) => void;
}

const STORAGE_KEY = "theme-variant";
const VALID_VARIANTS = THEME_VARIANTS.map((t) => t.value);
const DEFAULT_VARIANT: ThemeVariant = "classic";

const ThemeVariantContext = createContext<ThemeVariantContextValue>({
  variant: DEFAULT_VARIANT,
  setVariant: () => {},
});

export function ThemeVariantProvider({ children }: { children: React.ReactNode }) {
  const [variant, setVariantState] = useState<ThemeVariant>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return VALID_VARIANTS.includes(stored as ThemeVariant) ? (stored as ThemeVariant) : DEFAULT_VARIANT;
  });

  const setVariant = useCallback((v: ThemeVariant) => {
    setVariantState(v);
    localStorage.setItem(STORAGE_KEY, v);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (variant === "classic") {
      root.removeAttribute("data-variant");
    } else {
      root.setAttribute("data-variant", variant);
    }
  }, [variant]);

  return (
    <ThemeVariantContext.Provider value={{ variant, setVariant }}>
      {children}
    </ThemeVariantContext.Provider>
  );
}

export function useThemeVariant() {
  return useContext(ThemeVariantContext);
}
